import {
   Cluster as ClusterType,
   useClusteredDataContext,
} from 'Guide/Schematics/Diagram/Providers/clusteredData';
import { iFixitHotSpotItem } from './iFixitHotSpotItem';
import { BasicCluster } from 'Guide/Schematics/Diagram/Generic/basicCluster';
import { KeyboardEventHandler, memo, useContext } from 'react';
import { ZoomContext } from 'Guide/Schematics/Diagram/Providers/d3Zoomable';
import { useDiagramTheme } from '../Providers/themes';

const MemoBasicCluster = memo(BasicCluster);

export function Cluster({ cluster }: { cluster: ClusterType<iFixitHotSpotItem> }) {
   const x = average(cluster.map(p => p.x));
   const scaledX = average(cluster.map(p => p.scaledX ?? 0));
   const y = average(cluster.map(p => p.y));
   const scaledY = average(cluster.map(p => p.scaledY ?? 0));
   const tabIndex = Math.max(...cluster.map(p => p.tabIndex ?? 0));

   const diagramTheme = useDiagramTheme();
   const clusterInner = diagramTheme.clusterInner;
   const clusterOuter = diagramTheme.clusterOuter;

   const clusteredDataContext = useClusteredDataContext<iFixitHotSpotItem>();
   const zoomContext = useContext(ZoomContext);

   const onClick = () => {
      const isSmallCluster = cluster.length <= 3;

      const zoomFactor = isSmallCluster
         ? clusteredDataContext.getFullDeclusterZoomFactor(cluster)
         : clusteredDataContext.getDeclusterZoomFactor(cluster);

      const newZoomFactor = zoomContext.d3Transform.k * zoomFactor;
      const newX = x * newZoomFactor;
      const newY = y * newZoomFactor;

      zoomContext.scaleAndCenterOn(newX, newY, newZoomFactor);
   };

   const onKeyDown: KeyboardEventHandler<SVGGElement> = event => {
      if (event.key === 'Enter') {
         onClick();
      }
   };

   return (
      <MemoBasicCluster
         scaledX={scaledX}
         scaledY={scaledY}
         text={cluster.length.toString()}
         outerCircleProps={{
            fill: clusterOuter,
            hoverFill: clusterInner,
            focusFill: clusterInner,
         }}
         innerCircleProps={{
            fill: clusterInner,
            hoverFill: clusterOuter,
            focusFill: clusterOuter,
         }}
         textProps={{
            dy: '0.35em',
            fill: clusterOuter,
            hoverFill: 'white',
            focusFill: 'white',
            fontSize: '14px',
            fontWeight: '600',
         }}
         groupProps={{
            focusable: 'true',
            role: 'group',
            tabIndex: tabIndex,
            onClick: onClick,
            onKeyDown: onKeyDown,
         }}
      />
   );
}

const average = (numbers: number[]) => sum(numbers) / (numbers.length || 1);

const sum = (numbers: number[]) => numbers.reduce((a, b) => a + b, 0);
